import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, AlertTriangle, Check, Eye, EyeOff } from 'lucide-react';
import { api, setToken, type Profile } from '../lib/api';
import { AppIcon } from './Logo';
import { randomAvatar } from './Avatar';
import {
  BirthDateFields,
  CountryField,
  ConsentFields,
  ageVerdict,
  consentReady,
  isBirthDateReady,
  Field,
  INPUT,
} from './ProfileFields';

interface Props {
  /** Con qué pestaña abre. La barra superior tiene un botón para cada una. */
  initialMode?: AuthMode;
  onClose: () => void;
  onAuthenticated: (profile: Profile) => void;
}

export type AuthMode = 'login' | 'register';

/**
 * Entrada y registro.
 *
 * El avatar NO se elige aquí. Antes ocupaba el primer tercio del formulario y
 * era lo primero que se veía al crear una cuenta, por delante del usuario y la
 * contraseña; ahora la cuenta nace con uno al azar y se afina desde el perfil,
 * con el editor completo y sin prisa. Quien juega sin cuenta sí escoge el suyo
 * al entrar a la sala, pero ese no sobrevive al cierre del navegador.
 */
export const AuthModal: React.FC<Props> = ({
  initialMode = 'register',
  onClose,
  onAuthenticated,
}) => {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const [birth, setBirth] = useState({ day: '', month: '', year: '' });
  const [country, setCountry] = useState('');
  const [consent, setConsent] = useState({ acceptTerms: false, guardianConsent: false });
  const verdict = useMemo(() => ageVerdict(birth, country), [birth, country]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [available, setAvailable] = useState<boolean | null>(null);
  const [googleAvailable, setGoogleAvailable] = useState(false);

  const firstFieldRef = useRef<HTMLInputElement>(null);
  const isRegister = mode === 'register';

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, [mode]);

  // El botón solo aparece si el servidor tiene credenciales de Google. Mostrar
  // un botón que lleva a una página de error es peor que no mostrarlo.
  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<{ google: boolean }>('/auth/providers');
        setGoogleAvailable(data.google);
      } catch {
        setGoogleAvailable(false);
      }
    })();
  }, []);

  // Cerrar con Escape: un modal que solo se cierra con el ratón estorba.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Disponibilidad del usuario, consultada mientras se escribe.
  useEffect(() => {
    if (!isRegister || username.trim().length < 3) {
      setAvailable(null);
      return;
    }
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const data = await api.post<{ available: boolean }>('/auth/check-username', { username });
          setAvailable(data.available);
        } catch {
          setAvailable(null);
        }
      })();
    }, 350);
    return () => clearTimeout(handle);
  }, [username, isRegister]);

  const canSubmit = useMemo(() => {
    if (busy) return false;
    if (!username || password.length < 8) return false;
    if (!isRegister) return true;
    return (
      available !== false &&
      isBirthDateReady(birth) &&
      Boolean(country) &&
      firstName.trim().length >= 2 &&
      lastName.trim().length >= 2 &&
      emailOk &&
      consentReady(verdict, consent)
    );
  }, [busy, username, password, isRegister, available, birth, country, firstName, lastName, emailOk, verdict, consent]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const path = isRegister ? '/auth/register' : '/auth/login';
      const body = isRegister
        ? {
            username,
            password,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            birthDate: `${birth.year}-${birth.month}-${birth.day}`,
            country,
            acceptTerms: consent.acceptTerms,
            guardianConsent: verdict?.ok && verdict.needsGuardian ? consent.guardianConsent : false,
            // Un aspecto inicial distinto para cada cuenta, sin pedir una
            // decisión más en el peor momento para pedirla.
            avatar: randomAvatar(),
          }
        : { username, password };

      const data = await api.post<{ token: string; profile: Profile }>(path, body);
      setToken(data.token);
      onAuthenticated(data.profile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo completar la operación.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/85 backdrop-blur-sm flex items-start sm:items-center-safe justify-center p-4 overflow-y-auto"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-title"
    >
      <div className="surface-raised bg-ink-900 w-full max-w-[26rem] my-auto animate-pop">
        <header className="flex items-start gap-3 px-6 pt-6 pb-5">
          <AppIcon size={32} />
          <div className="flex-1 min-w-0">
            <h2 id="auth-title" className="font-display font-semibold text-[1.0625rem] text-ink-50">
              {isRegister ? 'Crea tu cuenta' : 'Inicia sesión'}
            </h2>
            <p className="text-ink-400 text-[0.8125rem] leading-snug mt-0.5">
              {isRegister
                ? 'Gratis, sin tarjeta y sin límite de partidas.'
                : 'Bienvenido de vuelta.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="-mt-1 -mr-2 p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </header>

        <form onSubmit={submit} className="px-6 pb-6 space-y-4">
          {isRegister && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nombre">
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={40}
                  required
                  autoComplete="given-name"
                  className={INPUT}
                />
              </Field>
              <Field label="Apellido">
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={40}
                  required
                  autoComplete="family-name"
                  className={INPUT}
                />
              </Field>
            </div>
          )}

          <Field
            label="Usuario"
            hint={
              isRegister
                ? '3 a 20 caracteres: letras, números, punto o guion bajo. Es el nombre con el que te verán los demás.'
                : undefined
            }
          >
            <div className="relative">
              <input
                ref={firstFieldRef}
                value={username}
                // Se filtra al escribir con la MISMA regla que aplica el
                // servidor. Dejar teclear una tilde que luego se rechaza es
                // hacerle perder el tiempo a quien se está registrando.
                onChange={(e) =>
                  setUsername(
                    e.target.value
                      .normalize('NFD')
                      .replace(/[̀-ͯ]/g, '')
                      .replace(/[^A-Za-z0-9_.]/g, ''),
                  )
                }
                autoComplete="username"
                required
                maxLength={20}
                className={`${INPUT} pr-10`}
              />
              {available !== null && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                  {available ? (
                    <Check className="w-4 h-4 text-success-400" aria-label="Disponible" />
                  ) : (
                    <X className="w-4 h-4 text-danger-400" aria-label="No disponible" />
                  )}
                </span>
              )}
            </div>
          </Field>

          {isRegister && (
            <Field label="Correo electrónico" hint="Obligatorio. No se muestra a nadie.">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                inputMode="email"
                required
                maxLength={254}
                placeholder="tu@correo.com"
                aria-invalid={email.trim() !== '' && !emailOk}
                className={INPUT}
              />
              {email.trim() !== '' && !emailOk && (
                <span className="block text-[0.6875rem] text-danger-400 mt-1">Revisa el correo: le falta algo.</span>
              )}
            </Field>
          )}

          <Field label="Contraseña" hint={isRegister ? 'Mínimo 8 caracteres' : undefined}>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={isRegister ? 'new-password' : 'current-password'}
                required
                minLength={8}
                className={`${INPUT} pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-ink-500 hover:text-ink-200 transition-colors"
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>

          {isRegister && (
            <>
              <BirthDateFields value={birth} onChange={setBirth} />
              <CountryField value={country} onChange={setCountry} />
              <p className="text-[0.6875rem] leading-relaxed text-ink-500">
                Tu nombre real, tu fecha de nacimiento y tu correo no se muestran a nadie:
                en las partidas solo aparece tu usuario. La edad y el país los
                pedimos porque la edad mínima para tener cuenta depende de la ley de cada país.
              </p>
              <ConsentFields verdict={verdict} value={consent} onChange={setConsent} />
            </>
          )}

          {error && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-danger-400/30 bg-danger-400/10 px-3 py-2.5 text-danger-400 text-[0.8125rem]"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full flex items-center justify-center gap-2 h-11 rounded-lg font-semibold
                       bg-ember-500 hover:bg-ember-400 text-on-accent
                       disabled:bg-ink-800 disabled:text-ink-500 disabled:cursor-not-allowed transition-colors"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {isRegister ? 'Crear cuenta' : 'Iniciar sesión'}
          </button>

          {googleAvailable && (
            <>
              <div className="flex items-center gap-3 pt-1">
                <span className="h-px flex-1 bg-ink-700" />
                <span className="text-ink-500 text-[0.6875rem] uppercase tracking-wider">o</span>
                <span className="h-px flex-1 bg-ink-700" />
              </div>
              <a
                href="/api/auth/google"
                className="w-full flex items-center justify-center gap-2.5 h-11 rounded-lg text-sm font-semibold
                           bg-ink-850 hover:bg-ink-800 border border-ink-700 text-ink-100 transition-colors"
              >
                <GoogleMark />
                Continuar con Google
              </a>
            </>
          )}

          <p className="text-center text-[0.8125rem] text-ink-400 pt-1">
            {isRegister ? '¿Ya tienes cuenta?' : '¿No tienes cuenta?'}{' '}
            <button
              type="button"
              onClick={() => {
                setMode(isRegister ? 'login' : 'register');
                setError('');
              }}
              className="text-sky-400 hover:text-sky-300 font-medium"
            >
              {isRegister ? 'Iniciar sesión' : 'Crear una'}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};

/** Logotipo oficial de Google, en sus cuatro colores de marca. */
const GoogleMark: React.FC = () => (
  <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </svg>
);
